import type { ReactNode } from 'react';
import type { ObjectiveForecastView } from '@contracts';
import { formatShortDate } from '@ui/shared/format/date';
import { AdjustmentOfferCard } from './AdjustmentOfferCard';

/** Whole days from b to a (a - b): positive when a is later than b. Mirrors
 *  DashboardScreen.desktop.tsx's own daysBetween exactly. */
function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(a) - Date.parse(b)) / 86_400_000);
}

function confidenceTier(confidence: number): 'Low' | 'Moderate' | 'High' {
  if (confidence >= 70) return 'High';
  if (confidence >= 35) return 'Moderate';
  return 'Low';
}

/** Plain-language reading, always rendered as visible text (never a
 *  tooltip-only affordance, per SS7.4/AC-O15) — a display concern built
 *  from fields the domain layer already exposes (confidence + pace
 *  presence), not new domain math (ADR-0002 addendum D5's own framing). */
function confidenceReading(forecast: ObjectiveForecastView): string {
  if (forecast.paceActionsPerWeek === null) {
    return 'Not enough history yet to gauge confidence.';
  }
  if (forecast.confidence >= 70) {
    return 'Based on several weeks of steady pace.';
  }
  if (forecast.confidence >= 35) {
    return 'A few weeks of data so far — confidence is building.';
  }
  return 'Early days — only a little data so far.';
}

/**
 * "Realism Engine" persistent panel (design/screens/objective-create-desktop.md
 * SS7). Renders identically whether `forecast` came from the live draft
 * preview (`computeDraftForecastPreview`) or the real, committed
 * `getObjective` forecast — both are `ObjectiveForecastView`, so this
 * component never needs to know which source produced it (SS8's own framing:
 * "the forecast math is equally real/live in both cases").
 */
export function RealismEnginePanel({
  forecast,
  progress,
  targetDate,
  onApplySuggestion,
  onDismissSuggestion,
  suggestionDismissed,
  children,
}: {
  readonly forecast: ObjectiveForecastView;
  readonly progress: { readonly done: number; readonly total: number };
  readonly targetDate: string | null;
  readonly onApplySuggestion?: () => Promise<void> | void;
  readonly onDismissSuggestion?: () => void;
  readonly suggestionDismissed?: boolean;
  /** Linked objectives + visibility toggles render below the forecast
   *  content, passed in as children so this component doesn't need to know
   *  their shape. */
  readonly children?: ReactNode;
}) {
  return (
    <section
      aria-label="Realism Engine"
      style={{
        position: 'sticky',
        top: 'var(--space-6)',
        width: '380px',
        flex: 'none',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        alignSelf: 'flex-start',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: 'var(--type-label-size)',
            fontWeight: 'var(--type-label-weight)',
            letterSpacing: 'var(--type-label-tracking)',
            textTransform: 'uppercase',
            color: 'var(--hue-primary)',
          }}
        >
          Realism Engine
        </span>
        <StatusBadge forecast={forecast} />
      </div>

      {forecast.status === 'paused' ? (
        <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          This objective is paused. Resume it from the Status control above to see its forecast again.
        </p>
      ) : (
        <ForecastBody
          forecast={forecast}
          progress={progress}
          targetDate={targetDate}
          onApplySuggestion={onApplySuggestion}
          onDismissSuggestion={onDismissSuggestion}
          suggestionDismissed={suggestionDismissed}
        />
      )}

      {children}
    </section>
  );
}

function StatusBadge({ forecast }: { readonly forecast: ObjectiveForecastView }) {
  let label: string;
  let color: string;
  if (forecast.status === 'paused') {
    label = 'Paused';
    color = 'var(--text-muted)';
  } else if (forecast.status === 'not_enough_data' || forecast.paceActionsPerWeek === null) {
    label = 'Not enough data yet';
    color = 'var(--text-muted)';
  } else if (forecast.isSlipping) {
    label = 'Shift target';
    color = 'var(--state-caution)';
  } else {
    label = 'Plausible';
    color = 'var(--hue-primary)';
  }

  return (
    <span
      style={{
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        color,
        background: 'var(--surface-hover)',
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function ForecastBody({
  forecast,
  progress,
  targetDate,
  onApplySuggestion,
  onDismissSuggestion,
  suggestionDismissed,
}: {
  readonly forecast: ObjectiveForecastView;
  readonly progress: { readonly done: number; readonly total: number };
  readonly targetDate: string | null;
  readonly onApplySuggestion?: () => Promise<void> | void;
  readonly onDismissSuggestion?: () => void;
  readonly suggestionDismissed?: boolean;
}) {
  const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <>
      {forecast.forecastFinishDate && targetDate && (
        <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>
          Projected finish · {formatShortDate(forecast.forecastFinishDate)}{' '}
          {(() => {
            const delta = daysBetween(targetDate, forecast.forecastFinishDate);
            return delta >= 0 ? (
              <span style={{ color: 'var(--hue-primary)' }}>{delta} days early</span>
            ) : (
              <span style={{ color: 'var(--text-secondary)' }}>{Math.abs(delta)} days past target</span>
            );
          })()}
        </p>
      )}

      <div
        role="img"
        aria-label={`${progress.done} of ${progress.total} actions done`}
        style={{ marginTop: 'var(--space-3)', height: '6px', background: 'var(--border-subtle)', borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}
      >
        <div style={{ height: '100%', width: `${percent}%`, background: 'var(--hue-primary-vivid)', borderRadius: 'var(--radius-pill)' }} />
      </div>
      <p className="num" style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
        {progress.done} of {progress.total} actions done
      </p>

      <div style={{ marginTop: 'var(--space-4)', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-3)' }}>
        <StatTile label="Weeks required" value={forecast.paceActionsPerWeek !== null ? `${forecast.paceActionsPerWeek}/wk` : '—'} />
        <StatTile label="At cadence" value={forecast.cadenceGoalPerWeek !== null ? `${forecast.cadenceGoalPerWeek}/wk` : '—'} />
        <StatTile label="Buffer" value={forecast.bufferWeeks !== null ? formatBuffer(forecast.bufferWeeks) : '—'} />
        <StatTile label="Confidence" value={confidenceTier(forecast.confidence)} />
      </div>
      <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
        {confidenceReading(forecast)}
      </p>

      {forecast.actualVsCadenceGoal && (
        <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Actual pace {forecast.actualVsCadenceGoal.actualPerWeek}/wk vs. your stated goal of{' '}
          {forecast.actualVsCadenceGoal.goalPerWeek}/wk — {forecast.actualVsCadenceGoal.aheadOfGoal ? 'ahead of your own goal' : 'behind your own goal'}.
        </p>
      )}

      {forecast.isSlipping && forecast.suggestion && !suggestionDismissed && (
        <AdjustmentOfferCard
          title={`Move target to ${formatShortDate(forecast.suggestion.suggestedTargetDate)}`}
          detail="keeps this within reach"
          suggestedTargetDate={forecast.suggestion.suggestedTargetDate}
          onApply={() => onApplySuggestion?.()}
          onDismiss={onDismissSuggestion}
        />
      )}
    </>
  );
}

function formatBuffer(bufferWeeks: number): string {
  if (bufferWeeks >= 0) return `+${bufferWeeks}w`;
  return `${bufferWeeks}w`;
}

function StatTile({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div style={{ background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }}>
      <div
        className="num"
        style={{ fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 'var(--space-1)',
          fontSize: 'var(--type-label-size)',
          fontWeight: 'var(--type-label-weight)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </div>
    </div>
  );
}
